import React, { useEffect, useState } from "react";
import { backtestAPI, strategyAPI } from "../services/api";
import BacktestResults from "../components/BacktestResults";
import { useBrokers, BrokerPickModal } from "../components/BrokerPicker";

const RANGES = ["1 Month", "3 Months", "6 Months", "1 Year", "2 Years", "Custom Range"];

// Keep the crypto portal fully separate from the NSE portal: the crypto host only
// ever lists crypto_* strategies; the NSE host hides crypto ones.
const IS_CRYPTO_PORTAL = typeof window !== "undefined" && /^crypto\./i.test(window.location.hostname || "");
const portalStrategies = (list) => (Array.isArray(list) ? list : []).filter(s => {
  const crypto = /^crypto/.test((s && s.assetClass) || "");
  return IS_CRYPTO_PORTAL ? crypto : !crypto;
});

export default function StrategyBacktest() {
  const [strategies, setStrategies] = useState([]);
  const [selected, setSelected] = useState([]);
  const [range, setRange] = useState("1 Month");
  const [interval, setInterval] = useState("5minute");
  const [credits, setCredits] = useState({ used: 0, total: 50 });
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [approving, setApproving] = useState(false);
  const brokers = useBrokers();
  const [pickBroker, setPickBroker] = useState(false); // show broker picker for approve & deploy Live

  useEffect(() => {
    const preselectId = new URLSearchParams(window.location.search).get("strategy");
    strategyAPI.getAll().then(r => {
      const list = portalStrategies(r.data);
      setStrategies(list);
      if (preselectId) {
        const match = list.find(s => s._id === preselectId);
        if (match) setSelected([match]);
      }
    }).catch(() => {
      // Never invent strategies — show none if the list can't be loaded.
      setStrategies([]);
    });
    backtestAPI.getCredits().then(r => setCredits(r.data)).catch(() => {});
  }, []);

  const toggleStrategy = (s) => {
    setSelected(prev => prev.find(x => x._id === s._id) ? prev.filter(x => x._id !== s._id) : [...prev, s]);
    setDropOpen(false);
  };

  const runBacktest = async () => {
    if (!selected.length) return alert("Please select at least one strategy");
    setRunning(true); setResults(null); setError(null);
    try {
      const res = await backtestAPI.run({ strategyIds: selected.map(s => s._id), range, customFrom, customTo, interval });
      setResults(res.data);
    } catch (e) {
      // Never fabricate results. Surface the real reason (e.g. expired Upstox
      // token → no real option data) so the number isn't trusted blindly.
      setError(e?.response?.data?.message || e?.message || "Backtest failed. Please try again.");
    }
    setRunning(false);
  };

  // The single selected strategy, when it's an AI-generated one still awaiting
  // approval — its backtest is what unlocks live deployment.
  const pendingApproval = selected.length === 1 && selected[0].aiGenerated && !selected[0].aiApproved ? selected[0] : null;

  // Run the approve (and optional Live deploy on the chosen broker). brokerId is
  // undefined for approve-only or a single-broker setup.
  const runApprove = async (thenDeployLive, brokerId) => {
    setApproving(true);
    try {
      await strategyAPI.approve(pendingApproval._id);
      if (thenDeployLive) {
        await strategyAPI.deploy(pendingApproval._id, "Live", brokerId);
        const bname = brokerId ? (brokers.find(b => b._id === brokerId)?.name || "broker") : "";
        alert("Approved & deployed Live!" + (bname ? " · " + bname : ""));
      } else {
        alert("Approved. You can now deploy it Live from Strategies.");
      }
      window.location.href = "/strategies";
    } catch (e) {
      alert("Failed: " + (e?.response?.data?.message || e?.message || "Try again."));
      setApproving(false);
    }
  };

  const approve = async (thenDeployLive) => {
    if (!pendingApproval) return;
    if (!thenDeployLive) return runApprove(false);
    // Always open the Go Live modal — broker choice + Terms & Conditions accept.
    setPickBroker(true);
  };

  const C = {
    card: { background: "#fff", border: "1px solid #e8eaf0", borderRadius: 12, padding: "20px 24px", marginBottom: 16 },
    lbl: { fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" },
  };

  return (
    <div style={{ padding: "20px 24px", fontFamily: "Inter, sans-serif", background: "#eef2f7", minHeight: "100vh" }}>

      {/* Hero header */}
      <div className="page-hero" style={{ position: "sticky", top: 0, zIndex: 50, background: "linear-gradient(120deg,#4f46e5 0%,#2563eb 55%,#1d4ed8 100%)", borderRadius: 18, padding: "22px 26px", marginBottom: 18, color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16, boxShadow: "0 14px 34px rgba(37,99,235,0.26)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div className="hero-icon" style={{ width: 46, height: 46, borderRadius: 13, background: "rgba(255,255,255,0.16)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 23 }}>🔬</div>
          <div>
            <h1 style={{ fontSize: 23, fontWeight: 900, margin: 0, letterSpacing: "-0.4px" }}>Backtesting</h1>
            <div style={{ fontSize: 13, opacity: 0.85, marginTop: 4 }}>Test your strategies against historical data</div>
          </div>
        </div>
        <div style={{ textAlign: "right", background: "rgba(255,255,255,0.14)", borderRadius: 12, padding: "10px 18px" }}>
          <div style={{ fontSize: 11, opacity: 0.85, fontWeight: 600, letterSpacing: "0.05em" }}>CREDITS</div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{credits.unlimited ? <>∞ <span style={{ fontSize: 12, opacity: 0.8, fontWeight: 500 }}>Unlimited</span></> : <>{(credits.total || 0) - (credits.used || 0)} <span style={{ fontSize: 12, opacity: 0.8, fontWeight: 500 }}>/ {credits.total || 0} left</span></>}</div>
        </div>
      </div>

      {/* Top control card */}
      <div style={C.card}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4, color: "#1a1a2e" }}>Choose Strategies to Backtest</h2>
        <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 16 }}>Pick one or more strategies, select a time range, and run a backtest to view performance.</p>

        <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
          {/* Strategy dropdown */}
          <div style={{ minWidth: 220, maxWidth: "100%", flex: "1 1 220px", position: "relative" }}>
            <div onClick={() => setDropOpen(!dropOpen)} style={{ border: "1px solid #e8eaf0", borderRadius: 8, padding: "9px 14px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13, background: "#fff" }}>
              <span style={{ color: selected.length ? "#1f2937" : "#9ca3af" }}>{selected.length ? `${selected.length} Selected` : "Select strategies..."}</span>
              <span style={{ color: "#9ca3af" }}>▾</span>
            </div>
            {dropOpen && (
              <div style={{ position: "absolute", top: "105%", left: 0, right: 0, background: "#fff", border: "1px solid #e8eaf0", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.1)", zIndex: 50, maxHeight: 200, overflowY: "auto" }}>
                {strategies.map(s => (
                  <div key={s._id} onClick={() => toggleStrategy(s)} style={{ padding: "10px 14px", cursor: "pointer", fontSize: 13, display: "flex", justifyContent: "space-between", background: selected.find(x => x._id === s._id) ? "#eff6ff" : "#fff", color: selected.find(x => x._id === s._id) ? "#2563eb" : "#374151" }}>
                    {s.name} {selected.find(x => x._id === s._id) && <span>✓</span>}
                  </div>
                ))}
              </div>
            )}
            {selected.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 8 }}>
                {selected.map(s => (
                  <span key={s._id} style={{ background: "#eff6ff", color: "#2563eb", borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                    {s.name} <span onClick={() => toggleStrategy(s)} style={{ cursor: "pointer", marginLeft: 2 }}>✕</span>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Range */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {RANGES.map(r => (
              <button key={r} onClick={() => setRange(r)} style={{ padding: "8px 13px", borderRadius: 7, border: "1px solid", fontSize: 12, fontWeight: 500, cursor: "pointer", background: range === r ? "#eff6ff" : "#fff", color: range === r ? "#2563eb" : "#6b7280", borderColor: range === r ? "#93c5fd" : "#e8eaf0" }}>{r}</button>
            ))}
          </div>

          {/* Candle interval */}
          <select
            value={interval}
            onChange={e => setInterval(e.target.value)}
            title="Candle timeframe used to simulate entries/exits. 1/2/3-min need 1-min data (≈Oct 2024 onward); 5/10/15-min reach back further."
            style={{ padding: "8px 10px", borderRadius: 7, border: "1px solid #e8eaf0", fontSize: 12, fontWeight: 500, color: "#374151", cursor: "pointer", background: "#fff" }}
          >
            <option value="1minute">1 min</option>
            <option value="2minute">2 min</option>
            <option value="3minute">3 min</option>
            <option value="5minute">5 min</option>
            <option value="10minute">10 min</option>
            <option value="15minute">15 min</option>
          </select>

          <button onClick={runBacktest} disabled={running} style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "9px 22px", fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", marginLeft: "auto", flex: "0 0 auto" }}>
            {running ? "Running..." : "Run Backtest"}
          </button>
        </div>

        {range === "Custom Range" && (
          <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={{ padding: "8px 12px", border: "1px solid #e8eaf0", borderRadius: 7, fontSize: 13, flex: "1 1 140px", maxWidth: "100%" }} />
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} style={{ padding: "8px 12px", border: "1px solid #e8eaf0", borderRadius: 7, fontSize: 13, flex: "1 1 140px", maxWidth: "100%" }} />
          </div>
        )}
        <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>Backtest Credit: <strong>{credits.unlimited ? "Unlimited (admin)" : `${credits.used}/${credits.total}`}</strong></div>
      </div>

      {/* Loading */}
      {running && (
        <div style={{ textAlign: "center", padding: 60 }}>
          <div style={{ width: 40, height: 40, border: "3px solid #e8eaf0", borderTop: "3px solid #2563eb", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 14px" }} />
          <div style={{ color: "#6b7280", fontSize: 13 }}>Running backtest simulation...</div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Error state — real backend message, never fabricated results */}
      {error && !running && (
        <div style={{ display: "flex", gap: 12, padding: "16px 18px", marginBottom: 16, borderRadius: 12, background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b" }}>
          <span style={{ fontSize: 20, lineHeight: 1 }}>⚠️</span>
          <div>
            <div style={{ fontWeight: 800, marginBottom: 4, fontSize: 14 }}>Backtest could not run</div>
            <div style={{ fontSize: 13, opacity: 0.95 }}>{error}</div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!results && !running && !error && (
        <div style={{ textAlign: "center", padding: 80, color: "#9ca3af", fontSize: 14 }}>
          Select a strategy and click Run Backtest to view results
        </div>
      )}

      {results && (
        <BacktestResults
          results={results}
          pendingApproval={pendingApproval}
          approving={approving}
          onApprove={approve}
          strategies={selected}
        />
      )}

      {pickBroker && (
        <BrokerPickModal
          brokers={brokers}
          strategyId={pendingApproval?._id}
          onConfirm={(brokerId) => { setPickBroker(false); runApprove(true, brokerId); }}
          onClose={() => setPickBroker(false)}
        />
      )}
    </div>
  );
}
